/*
 * Horizon Layered Slideshow - Custom Autoplay
 * Interval: 5000ms
 * Pause on hover: enabled
 * Manual navigation: preserved
 */

(() => {
  const INTERVAL = 5000;
  const SELECTOR = 'layered-slideshow-component';
  const initialized = new WeakSet();

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  function setupAutoplay(slideshow) {
    if (initialized.has(slideshow)) return;

    const tabs = slideshow.querySelectorAll(
      '[role="tab"][aria-selected]'
    );

    if (tabs.length < 2) return;

    initialized.add(slideshow);

    let timer = null;
    let hovering = false;
    let visible = true;
    let destroyed = false;

    function isPaused() {
      const activeElement = document.activeElement;

      const keyboardFocus =
        slideshow.contains(activeElement) &&
        activeElement?.matches(':focus-visible');

      return (
        hovering ||
        keyboardFocus ||
        !visible ||
        document.hidden ||
        reducedMotion.matches
      );
    }

    function clearTimer() {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function scheduleNext() {
      clearTimer();

      if (destroyed || isPaused()) return;

      timer = window.setTimeout(() => {
        if (!slideshow.isConnected) {
          clearTimer();
          return;
        }

        const currentTabs = Array.from(
          slideshow.querySelectorAll(
            '[role="tab"][aria-selected]'
          )
        );

        if (currentTabs.length < 2) return;

        const activeIndex = currentTabs.findIndex(
          (tab) =>
            tab.getAttribute('aria-selected') === 'true'
        );

        const nextIndex =
          ((activeIndex < 0 ? 0 : activeIndex) + 1) %
          currentTabs.length;

        if (typeof slideshow.select === 'function') {
          slideshow.select(nextIndex);
        }

        scheduleNext();
      }, INTERVAL);
    }

    function handlePointerEnter(event) {
      if (
        event.pointerType === 'mouse' ||
        event.pointerType === 'pen'
      ) {
        hovering = true;
        clearTimer();
      }
    }

    function handlePointerLeave() {
      hovering = false;
      scheduleNext();
    }

    function handleInteraction() {
      scheduleNext();
    }

    function handleVisibility() {
      scheduleNext();
    }

    function handleFocusOut() {
      window.setTimeout(scheduleNext, 0);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        visible = entries[0]?.isIntersecting ?? false;
        scheduleNext();
      },
      { threshold: 0.1 }
    );

    observer.observe(slideshow);

    slideshow.addEventListener(
      'pointerenter',
      handlePointerEnter
    );

    slideshow.addEventListener(
      'pointerleave',
      handlePointerLeave
    );

    slideshow.addEventListener(
      'click',
      handleInteraction
    );

    slideshow.addEventListener(
      'keydown',
      handleInteraction
    );

    slideshow.addEventListener(
      'focusin',
      handleInteraction
    );

    slideshow.addEventListener(
      'focusout',
      handleFocusOut
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    reducedMotion.addEventListener(
      'change',
      handleVisibility
    );

    slideshow.addEventListener(
      'shopify:block:select',
      handleInteraction
    );

    slideshow.addEventListener(
      'shopify:block:deselect',
      handleInteraction
    );

    // Clean up when Shopify removes the section.
    slideshow.cleanupCustomAutoplay = () => {
      destroyed = true;
      clearTimer();
      observer.disconnect();

      slideshow.removeEventListener(
        'pointerenter',
        handlePointerEnter
      );

      slideshow.removeEventListener(
        'pointerleave',
        handlePointerLeave
      );

      slideshow.removeEventListener(
        'click',
        handleInteraction
      );

      slideshow.removeEventListener(
        'keydown',
        handleInteraction
      );

      slideshow.removeEventListener(
        'focusin',
        handleInteraction
      );

      slideshow.removeEventListener(
        'focusout',
        handleFocusOut
      );

      slideshow.removeEventListener(
        'shopify:block:select',
        handleInteraction
      );

      slideshow.removeEventListener(
        'shopify:block:deselect',
        handleInteraction
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );

      reducedMotion.removeEventListener(
        'change',
        handleVisibility
      );
    };

    scheduleNext();
  }

  function initializeSlideshows(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(setupAutoplay);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeSlideshows()
    );
  } else {
    initializeSlideshows();
  }

  // Support Shopify theme editor section reloads.
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeSlideshows(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll(SELECTOR)
        .forEach((slideshow) => {
          slideshow.cleanupCustomAutoplay?.();
        });
    }
  );
})();
